import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Check, ChevronDown, ChevronRight, Layers, Loader2, Wand2,
} from 'lucide-react';
import { api } from '../../lib/api';
import { AUTO_RESOLVE_BATCH_ENABLED } from '../../config/featureFlags';
import {
  AUTO_RESOLVE_BATCH_ACTIONS, AUTO_RESOLVE_BATCH_BUCKETS, AUTO_RESOLVE_BATCH_MAX,
} from '../../config/autoResolve';
import BatchCard, { DismissButton, FoldReasonChip } from './AutoResolveBatchCard';
import {
  BATCH_POLL_REVIEW_MS, LOCKING, batchPollDelay, batchRequestRows, asArray, parseWhen, ageLabel, plural,
  newestFirst, CHANGE_TYPE_LABELS, CHIP, TONE, BUCKET_META, STATUS_META, SECTION_HEADING, FOCUS, PRIMARY_BTN,
  ERROR_BOX, errorText, isDisabledResponse, readStoredFlag, writeStoredFlag,
  FOLD, FOLD_META, DISMISSABLE, partitionBatches, foldReason, foldedSummary,
} from './autoResolveBatchShared';

// Batch auto-resolve (docs/auto-resolve-batch.md §3 and §7, EXPERIMENTAL).
// Shown on Edit Requests when the list is filtered to ONE school: pick the
// school's open requests, send them to one agent run, then review the single
// draft PR it opens and publish (or discard) it. Everything the agent wrote
// (summaries, questions, pages) is untrusted text: rendered as text only, and
// links are built only from http(s) URLs.
//
// The per-batch detail (BatchCard) is shared with the all-schools "Batch
// runs" strip (AutoResolveBatchStrip); the helpers live in
// autoResolveBatchShared.js and are re-exported here for existing callers.
// The Batches list follows the strip's attention-first rule
// (partitionBatches): batches that still want something first; published,
// dismissed, superseded and discarded ones fold into one summary row with
// Show all. Dismiss / Restore sit beside each card's status.

const { AUTO, REVIEW, HUMAN } = AUTO_RESOLVE_BATCH_BUCKETS;

export {
  BATCH_POLL_ACTIVE_MS, BATCH_POLL_REVIEW_MS, batchPollDelay, batchSteps, batchResults, batchRequestRows, previewPageUrl,
} from './autoResolveBatchShared';
export const BATCH_PANEL_COLLAPSED_KEY = 'nexsav-batch-panel-collapsed';

function normalize(res) {
  return {
    enabled: res?.enabled !== false,
    repo: typeof res?.repo === 'string' ? res.repo : null,
    candidates: Array.isArray(res?.candidates) ? res.candidates.filter((c) => c && c.id != null) : [],
    batches: Array.isArray(res?.batches) ? res.batches.filter((b) => b && b.id != null) : [],
  };
}

function readCollapsed() {
  return readStoredFlag(BATCH_PANEL_COLLAPSED_KEY, false);
}

function writeCollapsed(value) {
  writeStoredFlag(BATCH_PANEL_COLLAPSED_KEY, value);
}


// `onBatchesChanged` (optional): called after this panel started a batch or
// acted on one, so the page can refresh its list and the Batch runs strip.
export default function AutoResolveBatchPanel({ accountId, accountName, onBatchesChanged }) {
  const flagOn = Boolean(AUTO_RESOLVE_BATCH_ENABLED);
  const school = accountName || 'this school';
  const endpoint = `/api/admin/accounts/${encodeURIComponent(accountId || '')}/auto-resolve-batch`;
  const baseId = useId();

  // phase: loading (renders nothing) | ready | error | hidden (server flag off)
  const [phase, setPhase] = useState('loading');
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [refreshError, setRefreshError] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [tick, setTick] = useState(0);
  // Explicit include/exclude choices; anything not chosen uses its default.
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState(null); // { kind, batchId? }
  const [dispatchError, setDispatchError] = useState('');
  const [actionError, setActionError] = useState(null); // { batchId, message }
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [showAll, setShowAll] = useState(false);
  const [notice, setNotice] = useState('');

  const seqRef = useRef(0);
  const mountedRef = useRef(false);
  const hasDataRef = useRef(false);
  // Titles/pages of every request seen this session, so a batch can still
  // name its requests after they leave the open list (resolved on merge).
  const knownRef = useRef(new Map());
  // Where focus goes once a batch moved between the queue and the folded list.
  const focusRef = useRef(null);
  const changedRef = useRef(onBatchesChanged);
  changedRef.current = onBatchesChanged;

  const remember = useCallback((res) => {
    const known = knownRef.current;
    for (const c of res?.candidates || []) if (c?.id != null) known.set(String(c.id), { ...known.get(String(c.id)), ...c });
    for (const b of res?.batches || []) {
      for (const r of asArray(b?.requests)) if (r?.id != null) known.set(String(r.id), { ...known.get(String(r.id)), ...r });
    }
  }, []);

  // → the loaded state, or null when this load failed or was overtaken.
  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    setUpdating(true);
    try {
      const res = await api.get(endpoint);
      if (!mountedRef.current || seq !== seqRef.current) return null;
      remember(res);
      hasDataRef.current = true;
      const next = normalize(res);
      setData(next);
      setPhase('ready');
      setRefreshError(false);
      setLoadError('');
      return next;
    } catch (err) {
      if (!mountedRef.current || seq !== seqRef.current) return null;
      if (isDisabledResponse(err)) {
        setPhase('hidden');
      } else if (hasDataRef.current) {
        setRefreshError(true);
      } else {
        setLoadError(errorText(err, 'Couldn’t load batch mode.'));
        setPhase('error');
      }
      return null;
    } finally {
      if (mountedRef.current && seq === seqRef.current) {
        setUpdating(false);
        setTick((t) => t + 1);
      }
    }
  }, [endpoint, remember]);

  // First load, and a clean slate when the school changes.
  useEffect(() => {
    mountedRef.current = true;
    if (!flagOn || !accountId) return undefined;
    hasDataRef.current = false;
    knownRef.current = new Map();
    setPhase('loading');
    setData(null);
    setChoices({});
    setRefreshError(false);
    setDispatchError('');
    setActionError(null);
    setShowAll(false);
    setNotice('');
    load();
    return () => { mountedRef.current = false; };
  }, [flagOn, accountId, load]);

  const batches = useMemo(() => [...(data?.batches || [])].sort(newestFirst), [data]);
  const { queue: queuedBatches, folded: foldedBatches, counts: batchTally } = useMemo(() => partitionBatches(batches), [batches]);
  const candidates = data?.candidates || [];

  const foldToggleId = `${baseId}-folded-toggle`;
  const cardControlId = (id) => `${baseId}-fold-control-${id}`;

  // A batch that left the queue (or came back) takes focus with it: onto its
  // Dismiss / Restore when it has one on screen, else onto Show all / Show less.
  useEffect(() => {
    const ids = focusRef.current;
    if (!ids) return;
    focusRef.current = null;
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) { el.focus(); break; }
    }
  });

  // Self-scheduling poll: re-armed after every load (tick), stops once every
  // batch is in a terminal state. A failed refresh retries at the slow pace.
  const pollDelay = phase === 'ready'
    ? (batchPollDelay(batches) || (refreshError ? BATCH_POLL_REVIEW_MS : 0))
    : 0;
  useEffect(() => {
    if (!pollDelay) return undefined;
    const t = setTimeout(load, pollDelay);
    return () => clearTimeout(t);
  }, [pollDelay, tick, load]);

  const batchById = useMemo(() => new Map(batches.map((b) => [String(b.id), b])), [batches]);
  const lockingBatch = (c) => {
    if (c.batch_id == null) return null;
    const b = batchById.get(String(c.batch_id));
    return b && LOCKING.includes(b.status) ? b : null;
  };
  const isSelectable = (c) => c.classification !== HUMAN && !lockingBatch(c);
  const isChecked = (c) => {
    if (!isSelectable(c)) return false;
    const chosen = choices[c.id];
    if (typeof chosen === 'boolean') return chosen;
    return c.classification === AUTO || c.classification === REVIEW;
  };

  const selected = candidates.filter(isChecked);
  const selectedCount = selected.length;
  const humanCount = candidates.filter((c) => c.classification === HUMAN).length;
  const lockedCount = candidates.filter((c) => c.classification !== HUMAN && lockingBatch(c)).length;
  const overCap = selectedCount > AUTO_RESOLVE_BATCH_MAX;
  const showNoneHint = !overCap && selectedCount === 0 && candidates.length > 0;
  const canRun = phase === 'ready' && data?.enabled && selectedCount > 0 && !overCap && !busy;

  const toggle = (c) => setChoices((prev) => ({ ...prev, [c.id]: !isChecked(c) }));

  // Over the cap: keep the oldest requests, untick the rest (they go in the
  // next batch).
  const keepOldest = () => {
    const keep = new Set(
      [...selected]
        .sort((a, b) => (parseWhen(a.created_at)?.getTime() ?? 0) - (parseWhen(b.created_at)?.getTime() ?? 0))
        .slice(0, AUTO_RESOLVE_BATCH_MAX)
        .map((c) => c.id),
    );
    setChoices((prev) => {
      const next = { ...prev };
      for (const c of selected) next[c.id] = keep.has(c.id);
      return next;
    });
  };

  // Fold an action's response in right away (the reload follows).
  const applyResponse = (res) => {
    if (!res || typeof res !== 'object') return;
    if (Array.isArray(res.batches) || Array.isArray(res.candidates)) {
      remember(res);
      setData((prev) => normalize({ ...(prev || {}), ...res }));
      return;
    }
    const b = res.batch && typeof res.batch === 'object' ? res.batch : (res.id != null && res.status ? res : null);
    if (!b) return;
    remember({ batches: [b] });
    const ids = asArray(b.request_ids).map(String);
    setData((prev) => (prev ? {
      ...prev,
      batches: [b, ...prev.batches.filter((x) => String(x.id) !== String(b.id))],
      candidates: prev.candidates.map((c) => (ids.includes(String(c.id)) ? { ...c, batch_id: b.id } : c)),
    } : prev));
  };

  const dispatch = async () => {
    if (!canRun) return;
    const requestIds = selected.map((c) => c.id);
    seqRef.current += 1; // a poll already in flight must not undo what this action shows
    setBusy({ kind: AUTO_RESOLVE_BATCH_ACTIONS.DISPATCH });
    setDispatchError('');
    try {
      const res = await api.post(endpoint, { action: AUTO_RESOLVE_BATCH_ACTIONS.DISPATCH, requestIds });
      applyResponse(res);
      setChoices({});
      changedRef.current?.();
    } catch (err) {
      setDispatchError(errorText(err, 'Couldn’t start the batch.'));
    } finally {
      setBusy(null);
      await load();
    }
  };

  const FALLBACK = {
    [AUTO_RESOLVE_BATCH_ACTIONS.MERGE]: 'Couldn’t publish this batch.',
    [AUTO_RESOLVE_BATCH_ACTIONS.DISCARD]: 'Couldn’t discard this batch.',
    [AUTO_RESOLVE_BATCH_ACTIONS.REFRESH]: 'Couldn’t refresh this batch.',
    [AUTO_RESOLVE_BATCH_ACTIONS.DISMISS]: 'Couldn’t dismiss this batch.',
    [AUTO_RESOLVE_BATCH_ACTIONS.RESTORE]: 'Couldn’t restore this batch.',
  };
  const MOVED_TEXT = {
    [FOLD.PUBLISHED]: 'was published',
    [FOLD.DISMISSED]: 'was dismissed',
    [FOLD.SUPERSEDED]: 'is superseded by a later batch',
    [FOLD.DISCARDED]: 'was discarded',
  };
  const runAction = async (kind, batchId) => {
    if (busy) return false;
    seqRef.current += 1; // a poll already in flight must not undo what this action shows
    setBusy({ kind, batchId });
    setActionError(null);
    const before = batchById.get(String(batchId)) || null;
    const wasFolded = before ? foldReason(before) : null;
    // The batch moved between the queue and the folded list: say so, and
    // take focus along.
    const settle = (b) => {
      if (!before || !b) return false;
      const nowFolded = foldReason(b);
      if (Boolean(nowFolded) === Boolean(wasFolded)) return false;
      setNotice(nowFolded
        ? `The batch ${MOVED_TEXT[nowFolded] || 'was folded'} and moved under Show all.`
        : 'The batch is back in the list.');
      focusRef.current = nowFolded && !showAll ? [foldToggleId] : [cardControlId(batchId), foldToggleId];
      return true;
    };
    let ok = false;
    let settled = false;
    try {
      const res = await api.post(endpoint, { action: kind, batchId });
      applyResponse(res);
      if (res?.batch && typeof res.batch === 'object') settled = settle({ ...before, ...res.batch });
      ok = true;
      changedRef.current?.();
    } catch (err) {
      setActionError({ batchId, message: errorText(err, FALLBACK[kind] || 'Something went wrong.') });
    } finally {
      setBusy(null);
      const next = await load();
      if (ok && !settled && next) settle(next.batches.find((x) => String(x.id) === String(batchId)));
    }
    return ok;
  };

  // Beside each card's status: why it is folded, and Dismiss (queued, needs
  // a person / failed) or Restore (dismissed).
  const foldControls = (b, reason) => {
    const canDismiss = !reason && DISMISSABLE.includes(b.status);
    const canRestore = reason === FOLD.DISMISSED;
    const kind = canRestore ? AUTO_RESOLVE_BATCH_ACTIONS.RESTORE : AUTO_RESOLVE_BATCH_ACTIONS.DISMISS;
    const chip = reason && FOLD_META[reason]?.reasonChip ? <FoldReasonChip reason={reason} /> : null;
    if (!chip && !canDismiss && !canRestore) return null;
    return (
      <>
        {chip}
        {(canDismiss || canRestore) && (
          <DismissButton
            id={cardControlId(b.id)}
            restore={canRestore}
            school={school}
            busy={Boolean(busy && String(busy.batchId) === String(b.id) && busy.kind === kind)}
            disabled={Boolean(busy)}
            onClick={() => runAction(kind, b.id)}
          />
        )}
      </>
    );
  };

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(next);
  };

  const titleFor = (id) => knownRef.current.get(String(id)) || null;

  if (!flagOn || !accountId || phase === 'loading' || phase === 'hidden') return null;

  const titleId = `${baseId}-title`;
  const bodyId = `${baseId}-body`;
  const candidatesTitleId = `${baseId}-candidates`;
  const batchesTitleId = `${baseId}-batches`;
  const capHintId = `${baseId}-cap`;

  const inProgress = batches.filter((b) => LOCKING.includes(b.status)).length;
  const foldedText = foldedSummary(batchTally);
  const foldedListId = `${baseId}-folded`;
  const renderCard = (b, reason) => (
    <BatchCard
      key={b.id}
      batch={b}
      school={school}
      requestInfo={titleFor}
      busyKind={busy && String(busy.batchId) === String(b.id) ? busy.kind : null}
      anyBusy={Boolean(busy)}
      error={actionError && String(actionError.batchId) === String(b.id) ? actionError.message : ''}
      onAction={runAction}
      headerExtra={foldControls(b, reason)}
    />
  );
  const subtitle = phase === 'ready'
    ? [plural(candidates.length, 'open request'), inProgress ? `${plural(inProgress, 'batch', 'batches')} in progress` : null]
      .filter(Boolean).join(' · ')
    : 'Couldn’t load';

  const newest = batches[0];
  const newestMeta = newest ? STATUS_META[newest.status] : null;
  const liveText = newest && newestMeta
    ? `Newest batch (${plural(batchRequestRows(newest).length, 'request')}) ${newestMeta.sentence}.`
    : '';

  return (
    <section
      aria-labelledby={titleId}
      className="mb-4 rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden"
      data-testid="auto-resolve-batch-panel"
    >
      <div className="flex flex-wrap items-center gap-3 px-4 md:px-5 py-3.5">
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="text-[14px] font-semibold text-white leading-tight">
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-expanded={!collapsed}
              aria-controls={bodyId}
              className={`flex items-center gap-2.5 text-left rounded-lg ${FOCUS}`}
            >
              {collapsed
                ? <ChevronRight className="w-4 h-4 text-gray-500 shrink-0" aria-hidden="true" />
                : <ChevronDown className="w-4 h-4 text-gray-500 shrink-0" aria-hidden="true" />}
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-xl bg-[#B0FC38]/15 border border-[#B0FC38]/40 shrink-0" aria-hidden="true">
                <Layers className="w-3.5 h-3.5 text-[#B0FC38]" />
              </span>
              <span>Batch auto-resolve</span>
              <span className="px-1.5 py-0.5 rounded-full bg-[#B0FC38]/10 border border-white/10 text-[9px] font-semibold uppercase tracking-wide text-[#B0FC38]">Experimental</span>
            </button>
          </h2>
          <p className="mt-1 text-[12px] text-gray-400 leading-snug" data-testid="batch-subtitle">{subtitle}</p>
        </div>
        {updating && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500" data-testid="batch-updating">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> Updating…
          </span>
        )}
      </div>

      {/* Status region: the newest batch's state for screen readers, and the
          visible refresh-error line. */}
      <div role="status" aria-live="polite" aria-atomic="true" data-testid="batch-status">
        <span className="sr-only">{liveText}</span>
        {refreshError && (
          <p className="px-4 md:px-5 pb-2 text-[11px] text-amber-300/70" data-testid="batch-refresh-error">
            ⚠ Couldn’t refresh, retrying…
          </p>
        )}
      </div>
      {/* What an action just did to the list (a batch folded away or came back). */}
      <div role="status" aria-live="polite" className="sr-only" data-testid="batch-notice">{notice}</div>

      {!collapsed && (
        <div id={bodyId} className="border-t border-white/[0.06] px-4 md:px-5 py-4 flex flex-col gap-6">
          {phase === 'error' && (
            <div className={`${ERROR_BOX} flex flex-wrap items-center justify-between gap-2`} role="alert">
              <span>{loadError}</span>
              <button type="button" onClick={load} className={`underline underline-offset-2 hover:text-white rounded ${FOCUS}`}>Try again</button>
            </div>
          )}

          {phase === 'ready' && (
            <>
              <p className="text-[12px] text-gray-400 leading-relaxed">
                Sends the selected requests to one agent run on a copy of the{' '}
                <span className="text-gray-200">{school}</span> website
                {data.repo ? <> (<span className="font-mono text-gray-300">{data.repo}</span>)</> : null}. You review one draft
                change with a preview, then publish it or throw it away. Nothing goes live without you.
              </p>

              {!data.enabled && (
                <div className="px-3 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.06] text-[12px] text-gray-400" data-testid="batch-not-set-up">
                  Batch mode isn’t set up for {school}’s website yet.
                </div>
              )}

              {/* ── Candidates ─────────────────────────────────────────── */}
              <section aria-labelledby={candidatesTitleId}>
                <h3 id={candidatesTitleId} className={`${SECTION_HEADING} mb-2`}>Candidates</h3>
                {candidates.length === 0 ? (
                  <p className="text-[12px] text-gray-500" data-testid="batch-no-candidates">No open requests.</p>
                ) : (
                  <ul className="rounded-xl border border-white/[0.06] divide-y divide-white/[0.04]">
                    {candidates.map((c, i) => {
                      const cbId = `${baseId}-c${i}`;
                      const reasonId = `${baseId}-c${i}-reason`;
                      const locked = lockingBatch(c);
                      const selectable = isSelectable(c);
                      const bucket = BUCKET_META[c.classification] || { label: c.classification || 'Unclassified', tone: 'grey' };
                      const meta = [CHANGE_TYPE_LABELS[c.change_type] || c.change_type, ageLabel(c.created_at)].filter(Boolean);
                      const reason = locked ? 'Already in a batch that’s still open.' : (c.reason || '');
                      return (
                        <li key={c.id} className="flex items-start gap-3 px-3 py-2.5" data-testid={`batch-candidate-${c.id}`}>
                          <BatchCheckbox
                            id={cbId}
                            checked={isChecked(c)}
                            disabled={!selectable || Boolean(busy)}
                            onChange={() => toggle(c)}
                            describedBy={reason ? reasonId : undefined}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <label htmlFor={cbId} className={`text-[13px] font-medium break-words ${selectable ? 'text-white cursor-pointer' : 'text-gray-400'}`}>
                                {c.title || 'Untitled request'}
                              </label>
                              <span className={`${CHIP} ${TONE[bucket.tone]}`} data-testid="batch-classification">
                                <span className="sr-only">Classification: </span>{bucket.label}
                              </span>
                            </div>
                            <div className="mt-0.5 text-[11px] text-gray-500 flex flex-wrap gap-x-1.5">
                              {c.page && <span className="font-mono break-all">{c.page}</span>}
                              {meta.map((m) => <span key={m}><span aria-hidden="true">· </span>{m}</span>)}
                            </div>
                            {reason && <p id={reasonId} className="mt-0.5 text-[12px] text-gray-400 break-words">{reason}</p>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[12px] text-gray-400" data-testid="batch-count">
                    {selectedCount} selected · {humanCount} left out for a person
                    {lockedCount ? ` · ${lockedCount} already in a batch` : ''}
                  </p>
                  <button
                    type="button"
                    onClick={dispatch}
                    disabled={!canRun}
                    aria-describedby={overCap || showNoneHint ? capHintId : undefined}
                    aria-label={`Run batch of ${plural(selectedCount, 'request')} for ${school}`}
                    className={PRIMARY_BTN}
                  >
                    {busy?.kind === AUTO_RESOLVE_BATCH_ACTIONS.DISPATCH
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Starting…</>
                      : <><Wand2 className="w-3.5 h-3.5" aria-hidden="true" /> Run batch</>}
                  </button>
                </div>
                {overCap && (
                  <p id={capHintId} className="mt-2 text-[12px] text-amber-200/90" data-testid="batch-cap">
                    A batch takes at most {AUTO_RESOLVE_BATCH_MAX} requests, so the agent can give each one proper
                    attention. Untick {selectedCount - AUTO_RESOLVE_BATCH_MAX} or{' '}
                    <button type="button" onClick={keepOldest} className={`underline underline-offset-2 hover:text-white rounded ${FOCUS}`}>
                      keep the oldest {AUTO_RESOLVE_BATCH_MAX}
                    </button>
                    , and run the rest in the next batch.
                  </p>
                )}
                {showNoneHint && (
                  <p id={capHintId} className="mt-2 text-[12px] text-gray-500">Select at least one request to run a batch.</p>
                )}
                {dispatchError && <div className={`${ERROR_BOX} mt-2`} role="alert">{dispatchError}</div>}
              </section>

              {/* ── Batches ────────────────────────────────────────────── */}
              <section aria-labelledby={batchesTitleId}>
                <h3 id={batchesTitleId} className={`${SECTION_HEADING} mb-2`}>Batches</h3>
                {batches.length === 0 && (
                  <p className="text-[12px] text-gray-500" data-testid="batch-no-batches">No batches yet.</p>
                )}
                {batches.length > 0 && queuedBatches.length === 0 && (
                  <p className="text-[12px] text-gray-400" data-testid="batch-queue-empty">
                    Nothing waiting. Finished batches are folded below.
                  </p>
                )}
                {queuedBatches.length > 0 && (
                  <ol className="flex flex-col gap-3" data-testid="batch-queue">
                    {queuedBatches.map((b) => renderCard(b, null))}
                  </ol>
                )}
                {foldedBatches.length > 0 && (
                  <>
                    <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400" data-testid="batch-folded-summary">
                      <span>{foldedText}</span>
                      <span aria-hidden="true">·</span>
                      <button
                        id={foldToggleId}
                        type="button"
                        onClick={() => setShowAll((v) => !v)}
                        aria-expanded={showAll}
                        aria-controls={foldedListId}
                        className={`underline underline-offset-2 text-gray-300 hover:text-white rounded ${FOCUS}`}
                      >
                        {showAll ? 'Show less' : 'Show all'}
                        <span className="sr-only"> ({plural(foldedBatches.length, 'folded batch', 'folded batches')})</span>
                      </button>
                    </div>
                    {showAll && (
                      <ol id={foldedListId} aria-label="Folded batches" className="mt-3 flex flex-col gap-3" data-testid="batch-folded">
                        {foldedBatches.map((b) => renderCard(b, foldReason(b)))}
                      </ol>
                    )}
                  </>
                )}
              </section>
            </>
          )}
        </div>
      )}
    </section>
  );
}

// Same drawn checkbox as the Edit Requests table (DataTable), plus disabled.
function BatchCheckbox({ id, checked, disabled, onChange, describedBy }) {
  return (
    <span className="relative mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center align-middle">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-describedby={describedBy}
        className={`peer h-4 w-4 cursor-pointer appearance-none rounded-[4px] border border-white/20 bg-white/[0.04] transition-colors hover:border-white/40 checked:border-[#B0FC38] checked:bg-[#B0FC38] disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS}`}
      />
      <Check className="pointer-events-none absolute h-3 w-3 text-gray-900 opacity-0 peer-checked:opacity-100" strokeWidth={3} aria-hidden="true" />
    </span>
  );
}
